import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getComicById, getRelatedComics, formatNumber, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-950 mb-2">漫画不存在</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);
  const relatedComics = getRelatedComics(comic, 4);
  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 20);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://www.koreanmanhua.com/comic/${comic.id}`,
    image: comic.coverImage,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: Math.floor(comic.likes / 10),
    },
    genre: comic.tags,
  };

  return (
    <>
      <SEOHead
        title={comic.title}
        description={`${comic.title} - ${comic.description.slice(0, 120)}`}
        keywords={`${comic.title},${comic.tags.join(',')},免费韩漫,韩国漫画网,高清漫画`}
        canonicalUrl={`/comic/${comic.id}`}
        ogType="book"
        ogImage={comic.coverImage}
        schema={schema}
      />

      <main>
        <Navbar />

        <div className="pt-16">
          <div className="w-full px-4 md:px-6 py-4">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: categoryInfo?.name || '分类', href: `/category/${comic.category}` },
              { label: comic.title },
            ]} />
          </div>

          {/* Comic Info */}
          <section className="bg-background-100 border-y border-background-200/70" aria-label="漫画详情">
            <div className="w-full px-4 md:px-6 py-8 md:py-10">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
                  <div className="rounded-lg overflow-hidden aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画网免费韩漫`}
                      title={`${comic.title} - 高质量韩漫在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">{comic.title}</h1>
                  <div className="flex items-center gap-4 flex-wrap mb-4">
                    <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                      {comic.status}
                    </span>
                    <span className="text-sm text-foreground-600 flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                      <strong className="text-accent-500">{comic.rating}</strong> 分
                    </span>
                  </div>

                  <div className="flex items-center gap-6 mb-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                      {formatNumber(comic.views)} 次阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line"></i>
                      </div>
                      {formatNumber(comic.likes)} 人喜欢
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-list-2-line"></i>
                      </div>
                      {comic.chapters.length} 话
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 font-medium">{tag}</span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-circle-line"></i>
                      </div>
                      开始阅读
                    </Link>
                    <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-200 text-foreground-700 font-medium text-sm hover:bg-background-300 transition-all cursor-pointer whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line"></i>
                      </div>
                      收藏
                    </button>
                    <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-200 text-foreground-700 font-medium text-sm hover:bg-background-300 transition-all cursor-pointer whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-share-line"></i>
                      </div>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Chapter List */}
          <section className="py-8 md:py-10 bg-background-50" aria-label="章节列表">
            <div className="w-full px-4 md:px-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950">
                  <a href="#chapters" className="cursor-pointer">章节列表</a>
                </h2>
                <span className="text-sm text-foreground-500">共 {comic.chapters.length} 话</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {displayedChapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">{chapter.title}</span>
                    <span className="text-xs text-foreground-400 ml-2 shrink-0">{chapter.updatedAt}</span>
                  </Link>
                ))}
              </div>

              {comic.chapters.length > 20 && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllChapters(!showAllChapters)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {showAllChapters ? '收起' : `查看全部 ${comic.chapters.length} 话`}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-xs ${showAllChapters ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-8 md:py-10 bg-background-100" aria-label="相关推荐">
              <div className="w-full px-4 md:px-6">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {relatedComics.map((relComic) => (
                    <Link
                      key={relComic.id}
                      to={`/comic/${relComic.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                        <img
                          src={relComic.coverImage}
                          alt={`${relComic.title} - 韩国漫画网推荐`}
                          title={`${relComic.title} - 相关韩漫推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {relComic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{relComic.author}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs text-accent-500 font-medium flex items-center gap-0.5">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </div>
                          {relComic.rating}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </main>
    </>
  );
}